import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { createTheme, ThemeProvider as MUIThemeProvider } from '@mui/material/styles';
import { getAuthenticTheme } from '../theme/authenticTheme';
import defaultTheme from '../theme/theme';

const ThemeContext = createContext();

export const useThemeContext = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useThemeContext must be used within a ThemeContextProvider');
    }
    return context;
};

export const ThemeContextProvider = ({ children }) => {
    // 1. Initialize state from localStorage or system preference
    const [mode, setMode] = useState(() => {
        const savedMode = localStorage.getItem('themeMode');
        if (savedMode) return savedMode;
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    });

    // 2. Toggle function
    const toggleTheme = () => {
        setMode((prevMode) => {
            const newMode = prevMode === 'light' ? 'dark' : 'light';
            localStorage.setItem('themeMode', newMode);
            return newMode;
        });
    };

    // 3. Generate MUI Theme object dynamically
    // We use 'authenticTheme' logic but adapted for the mode.
    // If we are on specific pages that historically used 'defaultTheme' (like Login?),
    // we might need a strategy. For now, we apply "Authentic" (Vó Chica 3.0) globally as requested.
    const theme = useMemo(() => {
        return getAuthenticTheme(mode);
    }, [mode]);

    // 4. Inject CSS Variables for performance (avoiding JS re-renders for simple colors)
    useEffect(() => {
        const root = document.documentElement;
        // Backgrounds
        root.style.setProperty('--bg-default', theme.palette.background.default);
        root.style.setProperty('--bg-paper', theme.palette.background.paper);
        // Text
        root.style.setProperty('--text-primary', theme.palette.text.primary);
        root.style.setProperty('--text-secondary', theme.palette.text.secondary);
        // Semantics
        root.style.setProperty('--primary-main', theme.palette.primary.main);
        root.style.setProperty('--divider', theme.palette.divider);

        // Body class for global overrides if needed
        document.body.className = mode;
    }, [theme, mode]);

    return (
        <ThemeContext.Provider value={{ mode, toggleTheme }}>
            <MUIThemeProvider theme={theme}>
                {children}
            </MUIThemeProvider>
        </ThemeContext.Provider>
    );
};
